import React, { useEffect, useState } from "react";
import api from "../api";
import { X, Loader2, Receipt, CreditCard, Copy, Send, Undo2, Download, Pencil, Check, ArrowRightLeft } from "lucide-react";
import { toast } from "../hooks/use-toast";
import { formatSeat } from "./SeatMap";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return String(iso).slice(0, 16).replace("T", " "); }
};

const CHANNEL_STYLE = {
  "Online": "bg-[#eefafa] text-[#1fb0b0]",
  "Box office": "bg-amber-100 text-amber-700",
  "Comp": "bg-gray-100 text-gray-500",
};

function Ref({ label, value }) {
  if (!value) return null;
  const copy = () => { navigator.clipboard?.writeText(value); toast({ title: "Copied", description: `${label} copied to clipboard` }); };
  return (
    <div className="flex items-center justify-between gap-2 text-sm">
      <span className="text-[#8a97a8]">{label}</span>
      <button onClick={copy} data-testid={`copy-${label.toLowerCase().replace(/\s+/g, "-")}`} className="flex items-center gap-1.5 font-mono text-[#16243b] hover:text-[#1fb0b0] max-w-[60%] truncate" title={value}>
        <span className="truncate">{value}</span><Copy size={12} className="shrink-0" />
      </button>
    </div>
  );
}

export default function OrderDetailModal({ order: orderProp, orderId, manageable = false, onChanged, onClose }) {
  const [order, setOrder] = useState(orderProp || null);
  const [loading, setLoading] = useState(!orderProp);
  const [busy, setBusy] = useState("");
  const [reason, setReason] = useState("");
  const [amount, setAmount] = useState("");
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailVal, setEmailVal] = useState("");
  const [editAttendee, setEditAttendee] = useState(null); // ticket code being edited
  const [attendeeVal, setAttendeeVal] = useState("");
  const [resendNote, setResendNote] = useState("");
  const [showNote, setShowNote] = useState(false);
  const [events, setEvents] = useState([]);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState("");
  const [moveTier, setMoveTier] = useState("");
  const [moveNotify, setMoveNotify] = useState(true);

  // Always fetch full detail (incl. refund history) when we have an id
  const oid = orderProp?.order_id || orderId;
  const fetchOrder = () => api.get(`/business/orders/${oid}`).then(({ data }) => setOrder(data)).catch(() => {});
  useEffect(() => {
    if (manageable && oid) { setLoading(!orderProp); fetchOrder().finally(() => setLoading(false)); }
    else if (!orderProp && orderId) {
      setLoading(true);
      api.get(`/business/orders/${orderId}`).then(({ data }) => setOrder(data)).catch(() => setOrder(null)).finally(() => setLoading(false));
    }
  }, [orderId, orderProp, manageable, oid]);

  useEffect(() => {
    if (manageable) api.get("/business/products").then(({ data }) => setEvents(data || [])).catch(() => {});
  }, [manageable]);

  const targetEvent = events.find((e) => e.id === moveTarget);
  const doMove = async (codes) => {
    if (!moveTarget) { toast({ title: "Pick a target event first", variant: "destructive" }); return; }
    const label = codes ? `ticket ${codes[0]}` : "the whole order";
    if (!window.confirm(`Move ${label} to "${targetEvent?.title}"? They keep what they paid.`)) return;
    setBusy(codes ? `move-${codes[0]}` : "move");
    try {
      const { data } = await api.post(`/business/orders/${order.order_id}/move`, {
        product_id: moveTarget, tier_id: moveTier || "", notify: moveNotify, codes: codes || null,
      });
      const em = data.emailed ? " Buyer emailed a fresh ticket." : "";
      await after("Tickets moved", `${data.moved_count} ticket(s) moved to ${targetEvent?.title}.${em}`);
      if (!codes) { setMoveOpen(false); setMoveTarget(""); setMoveTier(""); }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === "need_tier") toast({ title: "Choose a ticket type", description: "No matching tier at the target event — pick one below.", variant: "destructive" });
      else toast({ title: "Couldn't move", description: detail || "Try again.", variant: "destructive" });
    } finally { setBusy(""); }
  };

  const after = async (title, desc) => { toast({ title, description: desc }); await fetchOrder(); onChanged?.(); };

  const refundOrder = async () => {
    if (!window.confirm(`Refund and cancel all remaining ticket(s) in this order?`)) return;
    setBusy("refund");
    try {
      const { data } = await api.post(`/business/orders/${order.order_id}/refund`, { reason });
      const em = data.email?.sent ? ` Buyer emailed at ${data.email.to}.` : "";
      await after("Order refunded", (data.refunded ? `${data.cancelled} ticket(s) cancelled & £${data.amount.toFixed(2)} refunded via Stripe.` : `${data.cancelled} ticket(s) cancelled (no card refund — not a Stripe payment).`) + em);
      setReason("");
    } catch (e) { toast({ title: "Error", description: e?.response?.data?.detail || "Could not refund", variant: "destructive" }); }
    finally { setBusy(""); }
  };

  const refundTicket = async (code) => {
    if (!window.confirm(`Refund and cancel ticket ${code}?`)) return;
    setBusy(code);
    try {
      const { data } = await api.post(`/business/orders/${order.order_id}/refund`, { reason, codes: [code] });
      await after("Ticket refunded", data.refunded ? `£${data.amount.toFixed(2)} refunded via Stripe.` : `Ticket cancelled (no card refund).`);
      setReason("");
    } catch (e) { toast({ title: "Error", description: e?.response?.data?.detail || "Could not refund ticket", variant: "destructive" }); }
    finally { setBusy(""); }
  };

  const refundAmount = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast({ title: "Enter an amount", variant: "destructive" }); return; }
    if (!window.confirm(`Refund £${amt.toFixed(2)} to the buyer? Tickets stay valid.`)) return;
    setBusy("amount");
    try {
      const { data } = await api.post(`/business/orders/${order.order_id}/refund-amount`, { amount: amt, reason });
      const em = data.email?.sent ? ` Buyer emailed at ${data.email.to}.` : "";
      await after("Refund recorded", (data.card_refunded ? `£${data.amount.toFixed(2)} refunded via Stripe.` : `£${data.amount.toFixed(2)} recorded (no card refund — ${data.reason}).`) + em);
      setAmount(""); setReason("");
    } catch (e) { toast({ title: "Error", description: e?.response?.data?.detail || "Refund failed", variant: "destructive" }); }
    finally { setBusy(""); }
  };

  const resendReceipt = async () => {
    setBusy("resend");
    try {
      const { data } = await api.post(`/business/orders/${order.order_id}/resend-receipt`, { note: resendNote.trim() || null });
      toast({ title: "Receipt sent", description: `Confirmation re-sent to ${data.to}.` });
      setResendNote(""); setShowNote(false);
    } catch (e) {
      toast({ title: "Couldn't send receipt", description: e?.response?.data?.detail || "Email may not be configured.", variant: "destructive" });
    } finally { setBusy(""); }
  };

  const downloadTickets = async () => {
    setBusy("download");
    try {
      const res = await api.get(`/business/orders/${order.order_id}/tickets.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tickets-${order.order_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast({ title: "Couldn't download tickets", description: e?.response?.data?.detail || "No valid tickets in this order.", variant: "destructive" });
    } finally { setBusy(""); }
  };

  const startEditEmail = () => { setEmailVal(order.buyer_email || ""); setEditingEmail(true); };
  const saveEmail = async () => {
    const em = emailVal.trim();
    if (!em || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) { toast({ title: "Enter a valid email", variant: "destructive" }); return; }
    setBusy("email");
    try {
      const { data } = await api.patch(`/business/orders/${order.order_id}/email`, { email: em });
      setEditingEmail(false);
      await after("Email updated", `Order email changed to ${data.email}. Use "Resend receipt" to send tickets to the new address.`);
    } catch (e) {
      toast({ title: "Couldn't update email", description: e?.response?.data?.detail || "Try again.", variant: "destructive" });
    } finally { setBusy(""); }
  };

  const startEditAttendee = (t) => { setEditAttendee(t.code); setAttendeeVal(t.attendee_name || ""); };
  const saveAttendee = async (code) => {
    const nm = attendeeVal.trim();
    if (!nm) { toast({ title: "Enter an attendee name", variant: "destructive" }); return; }
    setBusy(`att-${code}`);
    try {
      await api.patch(`/business/orders/${order.order_id}/tickets/${code}/attendee`, { name: nm });
      setEditAttendee(null);
      await after("Attendee updated", `Ticket ${code} attendee name corrected.`);
    } catch (e) {
      toast({ title: "Couldn't update attendee", description: e?.response?.data?.detail || "Try again.", variant: "destructive" });
    } finally { setBusy(""); }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="order-detail-modal">
      <div className="bg-white rounded-2xl w-full max-w-[560px] max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center p-5 border-b border-gray-100 sticky top-0 bg-white">
          <h3 className="font-bold text-[#16243b] flex items-center gap-2"><Receipt size={18} className="text-[#2ec9c9]" /> Order details</h3>
          <button onClick={onClose} data-testid="order-modal-close"><X className="text-[#5a6b80]" /></button>
        </div>
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[#2ec9c9]" size={30} /></div>
        ) : !order ? (
          <div className="py-16 text-center text-[#8a97a8]">Order not found.</div>
        ) : (
          <div className="p-5 space-y-5">
            <div>
              <p className="text-lg font-semibold text-[#16243b]" data-testid="order-buyer-name">{order.buyer_name || "—"}</p>
              {manageable && editingEmail ? (
                <div className="flex items-center gap-2 mt-1" data-testid="order-email-edit">
                  <input type="email" value={emailVal} onChange={(e) => setEmailVal(e.target.value)} data-testid="order-email-input"
                    className="flex-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm outline-none focus:border-[#2ec9c9]" placeholder="buyer@email.com" />
                  <button onClick={saveEmail} disabled={busy === "email"} data-testid="order-email-save"
                    className="rounded-lg px-3 py-1.5 text-sm font-medium bg-[#2ec9c9] text-white hover:bg-[#25b3b3] disabled:opacity-50 flex items-center gap-1">
                    {busy === "email" ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />} Save
                  </button>
                  <button onClick={() => setEditingEmail(false)} className="text-sm text-[#8a97a8] hover:text-[#5a6b80] px-1" data-testid="order-email-cancel">Cancel</button>
                </div>
              ) : (
                <p className="text-sm text-[#5a6b80] flex items-center gap-1.5">
                  <span>{order.buyer_email || "—"}{order.buyer_phone ? ` · ${order.buyer_phone}` : ""}</span>
                  {manageable && (
                    <button onClick={startEditEmail} data-testid="order-email-edit-btn" title="Fix a typo in the buyer's email"
                      className="text-[#8a97a8] hover:text-[#2ec9c9] shrink-0"><Pencil size={13} /></button>
                  )}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${CHANNEL_STYLE[order.channel] || "bg-gray-100 text-gray-500"}`}>{order.channel}</span>
                <span className="text-xs text-[#8a97a8]">{fmtDate(order.purchased_at)}</span>
                {order.status === "valid" && <span className="text-xs font-semibold px-2 py-1 rounded-full bg-green-100 text-green-700" data-testid="order-status-pill">Valid</span>}
                {order.status === "cancelled" && <span className="text-xs font-semibold px-2 py-1 rounded-full bg-red-100 text-red-600" data-testid="order-status-pill">Cancelled</span>}
                {order.status === "pending" && <span className="text-xs font-semibold px-2 py-1 rounded-full bg-gray-100 text-gray-500" data-testid="order-status-pill">Pending</span>}
              </div>
            </div>

            <div className="rounded-xl bg-[#f6f8fb] p-4">
              <p className="font-semibold text-[#16243b]">{order.product_title}{order.mixed_events ? " + others" : ""}</p>
              <p className="text-sm text-[#8a97a8]">{order.event_date ? fmtDate(order.event_date) : ""}{order.venue_name ? ` · ${order.venue_name}` : ""}</p>
              {order.mixed_events && <p className="text-[11px] text-[#1fb0b0] font-medium mt-1" data-testid="order-mixed-events">Tickets in this order are split across multiple events (see each ticket below).</p>}
            </div>

            {/* Price breakdown */}
            <div className="rounded-xl border border-gray-200 p-4 space-y-2" data-testid="order-breakdown">
              <div className="flex justify-between text-sm"><span className="text-[#5a6b80]">Tickets ({order.ticket_count})</span><span className="text-[#16243b] font-medium">£{order.subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-[#5a6b80]">Booking fee{order.fee_passed ? "" : " (absorbed)"}</span><span className="text-[#16243b] font-medium">£{order.booking_fee.toFixed(2)}</span></div>
              {order.discount_code && <div className="flex justify-between text-sm"><span className="text-[#5a6b80]">Promo code</span><span className="text-[#16243b] font-medium">{order.discount_code}</span></div>}
              <div className="flex justify-between text-base font-bold border-t border-gray-100 pt-2"><span className="text-[#16243b]">Total paid</span><span className="text-[#16243b]" data-testid="order-total">£{order.total.toFixed(2)}</span></div>
              {!order.fee_passed && order.platform_fee > 0 && <p className="text-[11px] text-[#8a97a8]">Platform fee of £{order.platform_fee.toFixed(2)} deducted from your payout.</p>}
            </div>

            {/* Stripe references */}
            {(order.stripe_session_id || order.stripe_payment_intent) ? (
              <div className="rounded-xl border border-gray-200 p-4 space-y-2" data-testid="order-stripe-refs">
                <p className="text-sm font-semibold text-[#16243b] flex items-center gap-1.5"><CreditCard size={15} className="text-[#635bff]" /> Stripe references</p>
                <Ref label="Payment Intent" value={order.stripe_payment_intent} />
                <Ref label="Session ID" value={order.stripe_session_id} />
              </div>
            ) : (
              <p className="text-xs text-[#8a97a8]">No Stripe reference — this order was taken via {order.channel === "Box office" ? "the box office" : order.channel === "Comp" ? "a comp" : "mock/test mode"}.</p>
            )}

            {/* Tickets in this order */}
            <div>
              <p className="text-sm font-semibold text-[#16243b] mb-2">Tickets in this order</p>
              <div className="space-y-1.5">
                {(order.tickets || []).map((t) => (
                  <div key={t.code} data-testid={`order-ticket-${t.code}`} className="flex items-center justify-between gap-2 text-sm bg-[#f9fbfd] rounded-lg px-3 py-2 border border-gray-100">
                    <div className="min-w-0">
                      <span className="font-mono text-[#16243b]">{t.code}</span>
                      <span className="text-[#8a97a8]"> · {t.tier_name}{t.seat ? ` · ${formatSeat(t.seat)}` : ""}</span>
                      {order.mixed_events && t.product_title && (
                        <span className="block text-[11px] text-[#1fb0b0] font-medium" data-testid={`ticket-event-${t.code}`}>{t.product_title}{t.event_date ? ` · ${fmtDate(t.event_date)}` : ""}</span>
                      )}
                      {t.move_history && t.move_history.length > 0 && (
                        <span className="block text-[11px] text-[#8a97a8] mt-0.5" data-testid={`ticket-moves-${t.code}`}>
                          {t.move_history.map((h, i) => (
                            <span key={i} className="block">↳ moved from <span className="text-[#5a6b80]">{h.from_title}</span>{h.from_date ? ` (${fmtDate(h.from_date)})` : ""} · {h.at ? fmtDate(h.at) : ""}</span>
                          ))}
                        </span>
                      )}
                      {editAttendee === t.code ? (
                        <span className="flex items-center gap-1.5 mt-1">
                          <input value={attendeeVal} onChange={(e) => setAttendeeVal(e.target.value)} data-testid={`attendee-input-${t.code}`}
                            placeholder="Attendee name" className="flex-1 rounded-md border border-gray-200 px-2 py-1 text-xs outline-none focus:border-[#2ec9c9]" />
                          <button onClick={() => saveAttendee(t.code)} disabled={busy === `att-${t.code}`} data-testid={`attendee-save-${t.code}`}
                            className="text-xs font-medium text-[#2ec9c9] hover:underline disabled:opacity-40">{busy === `att-${t.code}` ? "…" : "Save"}</button>
                          <button onClick={() => setEditAttendee(null)} className="text-xs text-[#8a97a8]">Cancel</button>
                        </span>
                      ) : (
                        <>
                          {t.attendee_name && <span className="text-[#5a6b80]"> · {t.attendee_name}</span>}
                          {manageable && t.status !== "cancelled" && (
                            <button onClick={() => startEditAttendee(t)} data-testid={`attendee-edit-${t.code}`} title="Correct the attendee's name"
                              className="ml-1 align-middle text-[#8a97a8] hover:text-[#2ec9c9]"><Pencil size={11} /></button>
                          )}
                        </>
                      )}
                      {t.status === "cancelled" && <span className="ml-1 text-xs text-red-500">(cancelled{t.refunded ? ", refunded" : ""})</span>}
                      {t.refund_note && <span className="block text-[11px] text-[#8a97a8] italic">“{t.refund_note}”</span>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[#16243b]">£{(t.amount_paid || 0).toFixed(2)}{t.refunded ? " ↩" : ""}</span>
                      {manageable && t.status !== "cancelled" && (
                        <>
                          <button onClick={() => refundTicket(t.code)} disabled={!!busy} data-testid={`refund-ticket-${t.code}`}
                            className="text-xs text-red-500 hover:underline disabled:opacity-40">{busy === t.code ? "…" : "Refund"}</button>
                          {moveOpen && moveTarget && (
                            <button onClick={() => doMove([t.code])} disabled={!!busy} data-testid={`move-ticket-${t.code}`}
                              className="text-xs text-[#1fb0b0] hover:underline disabled:opacity-40">{busy === `move-${t.code}` ? "…" : "Move"}</button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Refund history */}
            {(order.refunds && order.refunds.length > 0) && (
              <div data-testid="order-refund-history">
                <p className="text-sm font-semibold text-[#16243b] mb-2">Refund history</p>
                <div className="space-y-1.5">
                  {order.refunds.map((r) => (
                    <div key={r.id} className="text-sm bg-[#fff7f7] border border-red-100 rounded-lg px-3 py-2">
                      <div className="flex justify-between">
                        <span className="text-[#16243b] font-medium">{r.amount > 0 ? `−£${(r.amount).toFixed(2)}` : "Ticket cancelled"} <span className="text-xs text-[#8a97a8] font-normal">({r.kind}{r.card_refunded ? ", card refunded" : ", no card refund"})</span></span>
                        <span className="text-xs text-[#8a97a8]">{fmtDate(r.created_at)}</span>
                      </div>
                      {r.reason && <p className="text-[11px] text-[#5a6b80] italic mt-0.5">“{r.reason}”</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {manageable && order.status !== "cancelled" && (
              <div className="pt-3 border-t border-gray-100 space-y-3" data-testid="order-actions">
                <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="refund-reason"
                  placeholder="Optional refund reason / note (saved to history)…"
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
                <div className="rounded-xl bg-[#f9fbfd] border border-gray-100 p-3">
                  {!showNote ? (
                    <button onClick={() => setShowNote(true)} data-testid="add-resend-note"
                      className="text-sm text-[#1fb0b0] font-medium hover:underline flex items-center gap-1.5"><Send size={13} /> Add a note to the resent email (e.g. change of venue)</button>
                  ) : (
                    <div className="space-y-1.5" data-testid="resend-note-box">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-medium text-[#16243b]">Note for the customer (shown at the top of the email)</label>
                        <button onClick={() => { setShowNote(false); setResendNote(""); }} className="text-xs text-[#8a97a8] hover:text-[#5a6b80]">Remove</button>
                      </div>
                      <textarea value={resendNote} onChange={(e) => setResendNote(e.target.value)} data-testid="resend-note-input" rows={2}
                        placeholder="e.g. Please note the venue has changed to The Ferry, Glasgow."
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
                    </div>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button onClick={downloadTickets} disabled={!!busy} data-testid="order-download-tickets"
                    className="flex-1 gp-btn-outline rounded-xl py-2.5 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50">
                    {busy === "download" ? <Loader2 className="animate-spin" size={15} /> : <Download size={15} />} Download tickets
                  </button>
                  <button onClick={resendReceipt} disabled={!!busy} data-testid="order-resend-receipt"
                    className="flex-1 gp-btn-outline rounded-xl py-2.5 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50">
                    {busy === "resend" ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />} Resend receipt
                  </button>
                  <button onClick={refundOrder} disabled={!!busy} data-testid="order-refund"
                    className="flex-1 rounded-xl py-2.5 text-sm font-medium flex items-center justify-center gap-2 border border-red-200 text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50">
                    {busy === "refund" ? <Loader2 className="animate-spin" size={15} /> : <Undo2 size={15} />} Refund whole order
                  </button>
                </div>
                <div className="flex gap-2 items-center">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a97a8]">£</span>
                    <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="refund-amount-input"
                      placeholder="Custom amount" className="w-full rounded-xl border border-gray-200 pl-7 pr-3 py-2.5 text-sm outline-none focus:border-[#2ec9c9]" />
                  </div>
                  <button onClick={refundAmount} disabled={!!busy} data-testid="refund-amount-btn"
                    className="rounded-xl px-4 py-2.5 text-sm font-medium border border-gray-200 text-[#16243b] hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap">
                    {busy === "amount" ? "…" : "Refund amount"}
                  </button>
                </div>
                <p className="text-[11px] text-[#8a97a8]">Custom amount refunds to the buyer's card without cancelling their tickets.</p>

                <div className="rounded-xl bg-[#f9fbfd] border border-gray-100 p-3" data-testid="order-move-panel">
                  {!moveOpen ? (
                    <button onClick={() => setMoveOpen(true)} data-testid="order-move-toggle"
                      className="text-sm text-[#1fb0b0] font-medium hover:underline flex items-center gap-1.5"><ArrowRightLeft size={14} /> Move to another event / date</button>
                  ) : (
                    <div className="space-y-2.5">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-medium text-[#16243b]">Move this booking to another event</label>
                        <button onClick={() => { setMoveOpen(false); setMoveTarget(""); setMoveTier(""); }} className="text-xs text-[#8a97a8] hover:text-[#5a6b80]">Cancel</button>
                      </div>
                      <select value={moveTarget} onChange={(e) => { setMoveTarget(e.target.value); setMoveTier(""); }} data-testid="move-event-select"
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]">
                        <option value="">Choose target event…</option>
                        {events.filter((e) => order.mixed_events || e.id !== order.product_id).map((e) => (
                          <option key={e.id} value={e.id}>{e.title}{e.event_date ? ` · ${fmtDate(e.event_date)}` : ""}{e.venue_name ? ` · ${e.venue_name}` : ""}</option>
                        ))}
                      </select>
                      {targetEvent && (
                        <select value={moveTier} onChange={(e) => setMoveTier(e.target.value)} data-testid="move-tier-select"
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]">
                          <option value="">Auto-match ticket type by name</option>
                          {(targetEvent.ticket_tiers || []).map((t) => <option key={t.id} value={t.id}>{t.name} — £{Number(t.price).toFixed(2)}</option>)}
                        </select>
                      )}
                      <label className="flex items-center gap-2 text-xs text-[#5a6b80] cursor-pointer">
                        <input type="checkbox" checked={moveNotify} onChange={(e) => setMoveNotify(e.target.checked)} data-testid="move-notify" className="w-4 h-4 accent-[#2ec9c9]" />
                        Email the customer their new ticket &amp; QR
                      </label>
                      <button onClick={() => doMove(null)} disabled={!!busy || !moveTarget} data-testid="move-whole-order"
                        className="w-full rounded-lg py-2.5 text-sm font-medium flex items-center justify-center gap-2 bg-[#2ec9c9] text-white hover:bg-[#25b3b3] disabled:opacity-50">
                        {busy === "move" ? <Loader2 className="animate-spin" size={15} /> : <ArrowRightLeft size={15} />} Move whole order
                      </button>
                      <p className="text-[11px] text-[#8a97a8]">Keeps what they paid (no re-charge). Once a target is picked, a "Move" link also appears on each ticket above so you can move just one.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            {manageable && order.status === "cancelled" && (
              <div className="pt-3 border-t border-gray-100 flex gap-2" data-testid="order-actions">
                <button onClick={resendReceipt} disabled className="flex-1 gp-btn-outline rounded-xl py-2.5 text-sm font-medium flex items-center justify-center gap-2 opacity-50"><Send size={15} /> Resend receipt</button>
                <span className="flex-1 rounded-xl py-2.5 text-sm font-medium flex items-center justify-center gap-2 border border-red-200 text-red-400" data-testid="order-refund">Order cancelled</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
